import { useEffect, useState } from "react";

import CssBaseline from "@mui/material/CssBaseline";
import { Navigate, Outlet, useOutletContext } from "react-router-dom";

import { CfErrorPage } from "@/shared/components/common/CfErrorPage/CfErrorPage";
import { CfLoader } from "@/shared/components/common/CfLoader/CfLoader";
import DynamicIntlProvider from "@/shared/components/DynamicIntlProvider/DynamicIntlProvider";
import { type LANGUAGE_ID } from "@/shared/lang/lang.constants";

import { config } from "./app.config";
import { NoFarmsScreen } from "./common/components/NoFarmsScreen/NoFarmsScreen";
import { clearAllUserData } from "./common/services/auth/Auth.service";
import {
  type FarmSettingsTo,
  type FarmTo,
  getFarmSettings,
  getUserFarms,
} from "./generated/api/agroevidence";
import Localization from "./shared/services/Localization.service";
import LocalStorage from "./shared/services/LocalStorage.service";

import {
  type AsyncResult,
  type BootstrapCtx,
  type Config,
  type StoredUser,
} from "./app.types";

const useFarms = () => {
  const [farms, setFarms] = useState<AsyncResult<{ farms: FarmTo[] }>>({
    status: "pending",
  });

  useEffect(() => {
    if (farms.status === "success") return;

    getUserFarms()
      .then(({ data: farmsData }) => {
        setFarms({
          status: "success",
          farms: farmsData.toSorted(({ name: aName }, { name: bName }) =>
            aName && bName ? aName.localeCompare(bName) : 0,
          ),
        });
        LocalStorage.saveToLocalStorage(farmsData, "farms");
      })
      .catch((err) => {
        setFarms({ status: "error", code: err?.response?.status });
      });
  }, [farms.status]);

  return farms;
};

const useFarmSettings = (farmId?: string): FarmSettingsTo | undefined => {
  const [settings, setSettings] = useState<
    AsyncResult<{ settings: FarmSettingsTo }>
  >({ status: "pending" });

  useEffect(() => {
    if (!farmId) return;

    if (settings.status === "success") return;

    getFarmSettings(farmId)
      .then(({ data }) => {
        setSettings({ status: "success", settings: data });
        LocalStorage.saveToLocalStorage(data, "farmSettings");
      })
      .catch(() => {
        setSettings({ status: "error" });
      });
  }, [farmId, settings.status]);

  return settings.status === "success" ? settings.settings : undefined;
};

const useUser = (): StoredUser | Record<string, never> => {
  const user = LocalStorage.loadFromLocalStorage<StoredUser>("user");
  if (!user) {
    return {};
  }
  return user;
};

const ConfigResolver = ({
  children,
}: {
  children: (config: Config) => JSX.Element;
}) => children(config);

const ResolverBootstrap = () => {
  const farmsRes = useFarms();
  const user = useUser();

  if (farmsRes.status === "pending") {
    return <CfLoader size={20} />;
  }
  if (farmsRes.status === "error") {
    return (
      <DynamicIntlProvider locale={Localization.getLang() as LANGUAGE_ID}>
        <CssBaseline />
        <CfErrorPage error={{ isError: true, code: farmsRes.code ?? 500 }}>
          <Navigate replace to="/login" />
        </CfErrorPage>
      </DynamicIntlProvider>
    );
  }

  if (!user.id) {
    clearAllUserData();
    return <Navigate replace to="/login" />;
  }

  if (farmsRes.farms.length === 0) {
    return <NoFarmsScreen />;
  }

  return (
    <Outlet
      context={{ config, farms: farmsRes.farms, user } satisfies BootstrapCtx}
    />
  );
};

const useBootstrap = () => useOutletContext<BootstrapCtx>();

export {
  ConfigResolver,
  ResolverBootstrap,
  useBootstrap,
  useFarmSettings,
  useUser,
};
